import { type CSSProperties, type FormEvent, useEffect, useRef, useState } from 'react';
import { cornersCss, type UiTokens } from './look';

/** One message in a match's chat, as MOGZO's chat over a game shows it (M38b). */
export interface MatchChatMessage {
  id: string;
  from: string;
  mine: boolean;
  text: string;
  /** A quick phrase: drawn bolder. */
  phrase: boolean;
  deleted: boolean;
}

export type ChatLookName = 'panel' | 'compact';
export type ChatCornerName = 'bottom-left' | 'top-left' | 'bottom-right' | 'top-right';

/** A match's chat and how to send to it: from mogzo.com's chat API, or mogzo dev's own. */
export interface MatchChat {
  /** What the mode lets players send. */
  chat: 'phrases' | 'typed';
  look: ChatLookName;
  corner: ChatCornerName;
  phrases: string[];
  /** Oldest first. */
  messages: MatchChatMessage[];
  /** Why this player can't send, if they can't. */
  cantSend: string | null;
  /** Sends; resolves with why it wasn't sent, or null. */
  send: (text: string, phrase: boolean) => Promise<string | null>;
  /** Reports someone else's message (mogzo.com opens its report form). */
  report?: (messageId: string) => void;
}

const SEND = (
  <path d="M14.5 21.7a.5.5 0 0 0 .9 0l6.5-19a.5.5 0 0 0-.6-.6l-19 6.5a.5.5 0 0 0 0 .9l7.9 3.2a2 2 0 0 1 1.1 1.1zM21.9 2.1 10.9 13.1" />
);
const CLOSE = <path d="M18 6 6 18M6 6l12 12" />;
const CHAT = <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />;

function Glyph({ size = 18, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Sends a phrase or what's typed, and keeps why it wasn't sent. */
function useSender(chat: MatchChat) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  async function send(body: string, phrase: boolean) {
    if (!body.trim() || busy) return;
    setBusy(true);
    setProblem(null);
    const refused = await chat
      .send(body.trim(), phrase)
      .catch(() => 'Could not send it. Try again.');
    setBusy(false);
    if (refused) setProblem(refused);
    else if (!phrase) setText('');
  }
  return { text, setText, busy, problem, setProblem, send };
}

/**
 * A match's chat as a panel, in the game's look (M38b): a sheet over the bottom of the game on
 * a phone, a side panel on a computer. The game keeps going behind it.
 */
export function MatchChatPanel({
  t,
  chat,
  phone,
  onClose,
}: {
  t: UiTokens;
  chat: MatchChat;
  phone: boolean;
  onClose: () => void;
}) {
  const { text, setText, busy, problem, send } = useSender(chat);
  const list = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: stays at the newest message
  useEffect(() => {
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [chat.messages.length]);
  const c = t.colours;
  const place: CSSProperties = phone
    ? {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '62%',
        borderRadius: cornersCss({ ...t.corners, br: 0, bl: 0 }),
        paddingBottom: `calc(${Math.max(10, t.padding - 6)}px + env(safe-area-inset-bottom, 0px))`,
      }
    : {
        position: 'absolute',
        top: 12,
        right: 12,
        bottom: 12,
        width: `min(360px, calc(100% - 24px))`,
        borderRadius: cornersCss(t.corners),
      };
  return (
    <section
      aria-label="Match chat"
      style={{ ...t.panel, ...place, gap: 10, pointerEvents: 'auto' }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.15em', fontWeight: 700 }}>Match chat</h2>
          <p style={{ margin: 0, fontSize: '0.86em', color: c.muted }}>The game keeps going</p>
        </div>
        <button
          type="button"
          aria-label="Back to the game"
          onClick={onClose}
          style={{ ...t.quiet, minHeight: 40, padding: '0 10px' }}
          data-autofocus
        >
          <Glyph>{CLOSE}</Glyph>
        </button>
      </div>
      <div
        ref={list}
        role="log"
        aria-label="Messages"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {chat.messages.length === 0 && (
          <p style={{ margin: 'auto 0 0', color: c.muted, fontSize: '0.93em' }}>
            Say hi, or send a quick phrase.
          </p>
        )}
        {chat.messages.map((m, i) => {
          const runStart = i === 0 || chat.messages[i - 1]?.from !== m.from;
          return (
            <div
              key={m.id}
              style={{
                alignSelf: m.mine ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: m.mine ? 'flex-end' : 'flex-start',
                marginTop: runStart && i ? 4 : 0,
              }}
            >
              {runStart && !m.mine && (
                <span
                  style={{ margin: '0 0 2px 10px', fontSize: 12, fontWeight: 600, color: c.muted }}
                >
                  {m.from}
                </span>
              )}
              <span
                style={{
                  padding: '7px 12px',
                  borderRadius: 16,
                  background: m.mine ? c.accent : c.field,
                  color: m.mine ? c.onAccent : c.text,
                  fontWeight: m.phrase ? 700 : 400,
                  fontStyle: m.deleted ? 'italic' : 'normal',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {m.deleted ? 'Message deleted' : m.text}
              </span>
              {!m.mine && !m.deleted && chat.report && (
                <button
                  type="button"
                  onClick={() => chat.report?.(m.id)}
                  style={{
                    ...t.quiet,
                    minHeight: 24,
                    padding: '0 8px',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                  aria-label={`Report ${m.from}’s message`}
                >
                  Report
                </button>
              )}
            </div>
          );
        })}
      </div>
      {problem && (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: '8px 12px',
            borderRadius: 12,
            background: c.field,
            fontSize: '0.9em',
          }}
        >
          {problem}
        </p>
      )}
      {chat.cantSend ? (
        <p role="status" style={{ margin: 0, color: c.muted, fontSize: '0.9em' }}>
          {chat.cantSend}
        </p>
      ) : (
        <>
          <div
            role="toolbar"
            aria-label="Quick phrases"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
          >
            {chat.phrases.map((phrase) => (
              <button
                key={phrase}
                type="button"
                disabled={busy}
                onClick={() => send(phrase, true)}
                style={{
                  minHeight: 34,
                  padding: '0 12px',
                  borderRadius: 999,
                  border: `1px solid ${c.line}`,
                  background: c.field,
                  color: c.text,
                  font: 'inherit',
                  fontWeight: 600,
                  fontSize: '0.9em',
                  cursor: 'pointer',
                }}
              >
                {phrase}
              </button>
            ))}
          </div>
          {chat.chat === 'typed' && (
            <form
              onSubmit={(event: FormEvent) => {
                event.preventDefault();
                void send(text, false);
              }}
              style={{ display: 'flex', gap: 8 }}
            >
              <label
                htmlFor="mogzo-chat-box"
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                }}
              >
                Message this match
              </label>
              <input
                id="mogzo-chat-box"
                value={text}
                maxLength={1000}
                autoComplete="off"
                placeholder="Message this match"
                onChange={(event) => setText(event.target.value)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 44,
                  padding: '0 14px',
                  borderRadius: 22,
                  border: `1px solid ${c.line}`,
                  background: c.field,
                  color: c.text,
                  font: 'inherit',
                }}
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={busy || !text.trim()}
                style={{ ...t.primary, minHeight: 44, padding: '0 14px' }}
              >
                <Glyph>{SEND}</Glyph>
              </button>
            </form>
          )}
        </>
      )}
    </section>
  );
}

const SEE_THROUGH = 'rgba(0, 0, 0, 0.45)';
/** Lines over the game fade once they're this old, unless the player is typing. */
const FADE_MS = 12_000;

/** Lighter shades for names over a dark game, the same for the same name. */
const NAME_COLOURS = ['#ffdb6e', '#a6e4f5', '#f9b6cf', '#bdeca3', '#d5c4ff', '#ffc89a'];
function nameColour(name: string) {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) | 0;
  return NAME_COLOURS[Math.abs(hash) % NAME_COLOURS.length];
}

/**
 * A match's chat in the compact look (M38b): its last few lines in a corner of the game, white
 * on a dark see-through strip, and a Chat button; typing opens a slim bar with the phrases along
 * the bottom of the game (above the keyboard on a phone). The game keeps going.
 */
export function CompactChat({
  chat,
  typing,
  onTyping,
  showLines,
  avoidPauseButton,
}: {
  chat: MatchChat;
  typing: boolean;
  onTyping: (typing: boolean) => void;
  /** The player's "Show chat over the game" setting. */
  showLines: boolean;
  /** On a phone: MOGZO's pause button is at the top left. */
  avoidPauseButton: boolean;
}) {
  const { text, setText, busy, problem, setProblem, send } = useSender(chat);
  const [now, setNow] = useState(() => Date.now());
  const seen = useRef(new Map<string, number>());
  const input = useRef<HTMLInputElement>(null);
  for (const m of chat.messages) if (!seen.current.has(m.id)) seen.current.set(m.id, Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (typing) input.current?.focus();
    else setProblem(null);
  }, [typing, setProblem]);

  const lines = chat.messages
    .filter((m) => !m.deleted)
    .slice(-5)
    .filter((m) => typing || now - (seen.current.get(m.id) ?? 0) < FADE_MS);
  const [vertical, horizontal] = chat.corner.split('-') as ['top' | 'bottom', 'left' | 'right'];
  const feedPlace: CSSProperties = {
    position: 'absolute',
    [horizontal]: 10,
    ...(typing
      ? { bottom: chat.chat === 'typed' ? 104 : 56 }
      : vertical === 'top'
        ? { top: avoidPauseButton && horizontal === 'left' ? 66 : 12 }
        : { bottom: 52 }),
    width: 'min(62%, 420px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: horizontal === 'right' && !typing ? 'flex-end' : 'flex-start',
    gap: 3,
    pointerEvents: 'none',
  };
  const pillPlace: CSSProperties = {
    position: 'absolute',
    [horizontal]: 10,
    ...(vertical === 'top'
      ? { top: (avoidPauseButton && horizontal === 'left' ? 66 : 12) + lines.length * 24 + 6 }
      : { bottom: 14 }),
  };

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', fontFamily: 'inherit' }}>
      {(showLines || typing) && (
        <div role="log" aria-label="Match chat" aria-live="polite" style={feedPlace}>
          {lines.map((m) => (
            <div
              key={m.id}
              style={{
                maxWidth: '100%',
                boxSizing: 'border-box',
                padding: '3px 8px',
                borderRadius: 6,
                background: SEE_THROUGH,
                color: '#ffffff',
                fontSize: 13,
                lineHeight: '17px',
                fontWeight: m.phrase ? 700 : 400,
                textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
                overflowWrap: 'anywhere',
              }}
            >
              <b style={{ color: m.mine ? '#ffdb6e' : nameColour(m.from) }}>
                {m.mine ? 'You' : m.from}
              </b>{' '}
              {m.text}
            </div>
          ))}
        </div>
      )}
      {!typing && !chat.cantSend && (
        <button
          type="button"
          onClick={() => onTyping(true)}
          style={{
            ...pillPlace,
            pointerEvents: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            padding: '0 12px',
            borderRadius: 999,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: SEE_THROUGH,
            color: 'rgba(255, 255, 255, 0.9)',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Glyph size={15}>{CHAT}</Glyph>
          Chat
        </button>
      )}
      {typing && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, pointerEvents: 'auto' }}>
          {problem && (
            <p
              role="alert"
              style={{
                margin: '0 8px 6px',
                padding: '5px 10px',
                borderRadius: 6,
                background: 'rgba(120, 20, 20, 0.85)',
                color: '#fff',
                fontSize: 13,
              }}
            >
              {problem}
            </p>
          )}
          <div
            role="toolbar"
            aria-label="Quick phrases"
            style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '0 8px 6px' }}
          >
            {chat.phrases.map((phrase) => (
              <button
                key={phrase}
                type="button"
                disabled={busy}
                onClick={() => send(phrase, true)}
                style={{
                  flex: 'none',
                  height: 30,
                  padding: '0 12px',
                  borderRadius: 999,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.55)',
                  color: '#fff',
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {phrase}
              </button>
            ))}
          </div>
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void send(text, false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                onTyping(false);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px calc(6px + env(safe-area-inset-bottom, 0px))',
              background: 'rgba(0, 0, 0, 0.6)',
            }}
          >
            {chat.chat === 'typed' ? (
              <>
                <label
                  htmlFor="mogzo-compact-box"
                  style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                  }}
                >
                  Message this match
                </label>
                <input
                  ref={input}
                  id="mogzo-compact-box"
                  value={text}
                  maxLength={1000}
                  autoComplete="off"
                  enterKeyHint="send"
                  placeholder="Message this match"
                  onChange={(event) => setText(event.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 36,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    background: 'rgba(255, 255, 255, 0.12)',
                    color: '#fff',
                    font: 'inherit',
                    fontSize: 15,
                  }}
                />
                <button
                  type="submit"
                  disabled={busy || !text.trim()}
                  style={{
                    height: 36,
                    padding: '0 14px',
                    border: 0,
                    borderRadius: 8,
                    background: '#ffcb2f',
                    color: '#1b152f',
                    font: 'inherit',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Send
                </button>
              </>
            ) : (
              <span style={{ flex: 1, color: 'rgba(255, 255, 255, 0.8)', fontSize: 13 }}>
                Quick phrases only in this match
              </span>
            )}
            <button
              type="button"
              aria-label="Close chat"
              onClick={() => onTyping(false)}
              style={{
                width: 36,
                height: 36,
                display: 'grid',
                placeItems: 'center',
                border: 0,
                borderRadius: 8,
                background: 'transparent',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              <Glyph>{CLOSE}</Glyph>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * In the panel look on a phone, a new message from someone else shows briefly at the top of the
 * game (unless the player turned chat over the game off).
 */
export function ChatNote({
  chat,
  avoidPauseButton,
}: {
  chat: MatchChat;
  avoidPauseButton: boolean;
}) {
  const latest = chat.messages.findLast((m) => !m.mine && !m.deleted);
  const [shown, setShown] = useState<string | null>(null);
  const first = useRef(latest?.id ?? null);
  useEffect(() => {
    if (!latest || latest.id === first.current) return;
    setShown(latest.id);
    const timer = setTimeout(() => setShown(null), 4000);
    return () => clearTimeout(timer);
  }, [latest]);
  if (!latest || shown !== latest.id) return null;
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
        left: avoidPauseButton ? 68 : 12,
        right: 12,
        padding: '8px 12px',
        borderRadius: 14,
        background: 'rgba(27, 21, 47, 0.88)',
        color: '#fff',
        fontSize: 14,
        lineHeight: '19px',
        pointerEvents: 'none',
        overflowWrap: 'anywhere',
      }}
    >
      <b style={{ color: nameColour(latest.from) }}>{latest.from}</b> {latest.text}
    </div>
  );
}
